<!-- Router Outlet for routes like /support -->
<div *ngIf="currentRoute !== '' && currentRoute !== '/'">
  <router-outlet></router-outlet>
</div>

<!-- Loading State -->
<div
  *ngIf="(isCheckingAuth || isLoggingOut) && (currentRoute === '' || currentRoute === '/')"
  class="min-h-screen flex items-center justify-center bg-gray-100"
>
  <div class="text-center">
    <div
      class="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"
    ></div>
    <p class="text-gray-600">
      {{ isLoggingOut ? "Logging out..." : "Loading..." }}
    </p>
  </div>
</div>

<!-- Login Page -->
<app-login
  *ngIf="!isCheckingAuth && !isLoggingOut && !isLoggedIn && (currentRoute === '' || currentRoute === '/')"
  (loginSuccess)="onLoginSuccess($event)"
  (registerSuccess)="onRegisterSuccess($event)"
>
</app-login>

<!-- Main App (only shown when logged in and modal is not open) -->
<div
  *ngIf="!isCheckingAuth && !isLoggingOut && isLoggedIn && (currentRoute === '' || currentRoute === '/')"
  class="main-app"
  [class.menu-open]="isMobileMenuOpen"
>
  <!-- Navigation Bar -->
  <app-navigation
    #navigation
    [selectedVoice]="selectedVoice"
    [availableVoices]="availableVoices"
    [currentUser]="currentUser"
    [isLoggedIn]="isLoggedIn"
    [selectedUserLanguage]="selectedUserLanguage()"
    [selectedPartnerLanguage]="selectedPartnerLanguage()"
    [speakingSpeed]="speakingSpeed"
    [useDiarization]="useDiarization"
    (voiceSelected)="selectVoice($event)"
    (languageChanged)="onLanguageChanged($event)"
    (speedChanged)="onSpeedChange($event)"
    (diarizationToggled)="onDiarizationToggled($event)"
    (showLanguageSettings)="onShowLanguageSettings()"
    (showHistory)="onShowHistory()"
    (showWordUsage)="onShowWordUsage()"
    (showImageUsage)="onShowImageUsage()"
    (showAnalytics)="onShowAnalytics()"
    (showBuyWords)="onShowBuyWords()"
    (logout)="onLogout()"
    (menuStateChanged)="onMenuStateChanged($event)"
  >
  </app-navigation>

  <!-- OpenAI Quota Error Banner -->
  <div
    *ngIf="showQuotaErrorBanner"
    class="quota-error-banner bg-red-600 text-white p-4 mx-4 mt-2 rounded-lg shadow-lg animate-slide-down"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center flex-1">
        <svg
          class="w-6 h-6 mr-3 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clip-rule="evenodd"
          ></path>
        </svg>
        <div class="flex-1">
          <p class="font-semibold text-base md:text-lg">
            {{ quotaErrorMessage }}
          </p>
        </div>
      </div>
      <button
        (click)="dismissQuotaErrorBanner()"
        class="text-white hover:text-red-200 transition-colors ml-4 flex-shrink-0"
        aria-label="Dismiss"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>
  </div>

  <!-- Payment Success Banner -->
  <div
    *ngIf="showPaymentSuccessBanner"
    class="payment-success-banner bg-green-500 text-white p-4 mx-4 mt-2 rounded-lg shadow-lg animate-slide-down"
  >
    <div class="flex items-center justify-between">
      <div class="flex items-center">
        <svg class="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clip-rule="evenodd"
          ></path>
        </svg>
        <div>
          <p class="font-semibold text-lg">{{ paymentSuccessMessage }}</p>
        </div>
      </div>
      <button
        (click)="dismissPaymentSuccessBanner()"
        class="text-white hover:text-gray-200 transition-colors"
        aria-label="Close"
      >
        <svg
          class="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
    </div>
  </div>

  <div class="main-content">
    <div class="w-full grid grid-cols-1 lg:grid-cols-2 gap-2 md:gap-4">
      <!-- User → Partner Lane -->
      <section
        *ngIf="showUserSection()"
        class="border rounded-xl p-3 md:p-4 shadow"
      >
        <div class="mb-4">
          <!-- Hamburger menu button and logo/title - positioned above toggle button -->
          <div class="content-header-inline flex items-center mb-3">
            <button
              type="button"
              id="content-menu-button"
              class="content-hamburger-button flex-shrink-0"
              (click)="navigation.toggleMobileMenu($event)"
              (touchstart)="navigation.toggleMobileMenu($event)"
            >
              <svg
                class="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <!-- Spacer to push logo/title to center -->
            <div class="flex-1"></div>
            <!-- App Logo and Title - centered -->
            <div
              class="content-logo-title flex items-center gap-2 flex-shrink-0"
            >
              <img
                [src]="logoUrl"
                alt="TilHub Logo"
                class="content-logo w-10 h-10 object-contain"
              />
              <h1 class="content-title text-lg font-bold text-gray-900 m-0">
                {{ translations.translation || "Translation" }}
              </h1>
            </div>
            <!-- Spacer to balance the hamburger button on the left -->
            <div class="flex-1"></div>
          </div>
          <!-- Toggle Button replacing language direction label -->
          <button
            (click)="toggleBidirectionalSection()"
            type="button"
            class="w-full px-2 md:px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm md:text-base font-semibold transition-colors flex items-center justify-center gap-1 md:gap-2 mb-3 min-w-0 overflow-hidden"
            [title]="
              'Switch to ' +
              getPartnerLanguageName(selectedPartnerLanguage()) +
              ' → ' +
              getUserLanguageName(selectedUserLanguage())
            "
          >
            <svg
              class="w-4 h-4 md:w-5 md:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <span
              class="font-mono text-[10px] md:text-sm break-words whitespace-normal text-center flex-1 min-w-0"
              style="word-break: break-word; hyphens: auto; line-height: 1.2"
              >{{ getUserLanguageName(selectedUserLanguage()) }} →
              {{ getPartnerLanguageName(selectedPartnerLanguage()) }}</span
            >
            <span
              *ngIf="isDevMode()"
              class="ml-2 text-xs text-orange-600 font-bold"
              >Dev</span
            >
          </button>
          <!-- Conversation Topic Input (Hidden) -->
          <div class="mb-3" *ngIf="false" style="display: none;">
            <div class="flex items-center gap-2">
              <input
                type="text"
                [value]="conversationTopic()"
                (input)="conversationTopic.set($any($event.target).value)"
                [placeholder]="
                  translations.conversationTopicPlaceholder ||
                  'Conversation topic (e.g., dental lecture, medical conference)'
                "
                class="flex-1 p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxlength="50"
              />
              <button
                type="button"
                class="conversation-topic-info-btn flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none bg-transparent border-none p-0"
                [title]="
                  translations.conversationTopicHint ||
                  'Optional: Helps improve translation accuracy for specific topics'
                "
                (click)="showConversationTopicHelp()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="flex justify-between items-center w-full">
            <!-- Microphone Toggle Button with Recording Indicator -->
            <div class="flex items-center gap-2">
              <button
                (click)="toggleRecording('user')"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3"
                [class]="
                  recording && currentLane === 'user'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-blue-600 hover:bg-blue-700'
                "
                title="{{ translations.start }}"
              >
                <!-- Microphone Icon -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  [class.animate-pulse]="recording && currentLane === 'user'"
                >
                  <path
                    d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"
                  />
                  <path
                    d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"
                  />
                </svg>
              </button>
              <!-- Continuous Conversation Button -->
              <button
                (click)="openContinuousConversationModal()"
                type="button"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3 bg-blue-600 hover:bg-blue-700"
                title="{{
                  translations.continuousConversation ||
                    'Continuous Conversation'
                }}"
              >
                <!-- Chat/Conversation Icon -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
                  />
                  <path d="M7 9h2v2H7zm4 0h6v2h-6zm-4 4h6v2H7z" />
                </svg>
              </button>
              <!-- Bidirectional Conversation Button -->
              <button
                *ngIf="showUserSection()"
                (click)="openBidirectionalConversationModal()"
                type="button"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3 bg-blue-600 hover:bg-blue-700"
                title="{{
                  translations.bidirectionalConversation ||
                    'Bidirectional Conversation'
                }}"
              >
                <!-- Bidirectional Arrow Icon (<>) -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                >
                  <!-- Left arrow (<) -->
                  <path
                    d="M9 12L5 8M5 8L9 4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <!-- Right arrow (>) -->
                  <path
                    d="M15 12L19 8M19 8L15 4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <!-- Broadcast Translation Button -->
              <button
                (click)="openBroadcastTranslationModal()"
                type="button"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3 bg-blue-600 hover:bg-blue-700"
                title="{{
                  translations.broadcastTranslation || 'Broadcast Translation'
                }}"
              >
                <!-- Radio Antenna Icon -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <!-- Antenna Tower Base -->
                  <rect x="11" y="18" width="2" height="4" />
                  <!-- Antenna Mast -->
                  <line
                    x1="12"
                    y1="2"
                    x2="12"
                    y2="18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <!-- Antenna Top (dipole) -->
                  <line
                    x1="8"
                    y1="2"
                    x2="16"
                    y2="2"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <line
                    x1="8"
                    y1="2"
                    x2="8"
                    y2="4"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <line
                    x1="16"
                    y1="2"
                    x2="16"
                    y2="4"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <!-- Radiating Waves Left -->
                  <path
                    d="M12 18 Q8 16 4 14"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                  <path
                    d="M12 18 Q6 15 0 12"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                  <!-- Radiating Waves Right -->
                  <path
                    d="M12 18 Q16 16 20 14"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                  <path
                    d="M12 18 Q18 15 24 12"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <!-- Recording Indicator -->
              <span
                *ngIf="recording && currentLane === 'user'"
                class="text-red-600 font-semibold text-sm md:text-base animate-pulse"
              >
                Recording...
              </span>
            </div>

            <!-- Right side buttons: Show All and Clear History -->
            <div class="flex gap-2 ml-2 md:ml-0">
              <!-- Show All Conversations Button -->
              <button
                (click)="toggleConversationVisibility()"
                class="w-10 h-8 md:w-12 md:h-10 rounded-lg border transition-colors duration-200 flex-shrink-0 flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                [class]="
                  showAllConversations
                    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700'
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                "
                title="{{ translations.showAll }}"
              >
                <svg
                  *ngIf="!showAllConversations"
                  class="w-5 h-5 md:w-6 md:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  ></path>
                </svg>
                <svg
                  *ngIf="showAllConversations"
                  class="w-5 h-5 md:w-6 md:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                  ></path>
                </svg>
              </button>

              <!-- Clear History Button -->
              <button
                (click)="clearUserText()"
                class="w-10 h-8 md:w-12 md:h-10 rounded-lg bg-gray-500 text-white hover:bg-gray-600 flex-shrink-0 flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                title="{{ translations.clearHistory }}"
              >
                <svg
                  class="w-5 h-5 md:w-6 md:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  ></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Translation Spinner - More Visible Under Buttons -->
          <div
            *ngIf="
              (isProcessingTranslation || isProcessingPostTranslation) &&
              !isProcessingAudioGeneration &&
              !isProcessingOCR &&
              currentLane === 'user'
            "
            class="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-center gap-2"
          >
            <div
              class="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"
            ></div>
            <span class="text-orange-700 font-medium"
              >Translating to
              {{ getPartnerLanguageName(selectedPartnerLanguage()) }}...</span
            >
          </div>

          <!-- OCR Processing Spinner (User Section) -->
          <div
            *ngIf="isProcessingOCR && currentLane === 'user'"
            class="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center gap-2"
          >
            <div
              class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"
            ></div>
            <span class="text-purple-700 font-medium"
              >Reading image for
              {{ getUserLanguageName(selectedUserLanguage()) }}...</span
            >
          </div>

          <!-- Audio Generation Spinner (Second Cycle) - Blue background to distinguish from translation -->
          <div
            *ngIf="isProcessingAudioGeneration && currentLane === 'user'"
            class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center gap-2"
          >
            <div
              class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
            ></div>
            <span class="text-blue-700 font-medium"
              >🎵 Generating audio...</span
            >
          </div>

          <!-- Word Limit Error Message (Mobile-Friendly) -->
          <div
            *ngIf="wordLimitErrorMessage"
            class="mt-3 p-4 bg-red-50 border-2 border-red-500 rounded-lg"
          >
            <div class="flex items-start gap-3">
              <span class="text-2xl">⚠️</span>
              <div class="flex-1">
                <h3 class="text-red-800 font-bold text-lg mb-2">
                  {{ translations.wordLimitExceededTitle }}
                </h3>
                <p class="text-red-700 text-base mb-3">
                  {{ wordLimitErrorMessage }}
                </p>
                <div class="flex gap-2">
                  <button
                    *ngIf="!isIOS()"
                    (click)="onShowBuyWords()"
                    class="px-4 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700"
                  >
                    {{ translations.buyMoreWords }}
                  </button>
                  <button
                    (click)="wordLimitErrorMessage = null"
                    class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md font-semibold hover:bg-gray-400"
                  >
                    {{ translations.dismiss }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Text Input Section for User -->
          <div class="mt-4 p-3 bg-gray-50 rounded-lg border">
            <div
              class="w-full relative"
              style="position: relative; overflow: visible"
            >
              <textarea
                #userTextArea
                [(ngModel)]="userTextInput"
                placeholder="{{ translations.typeMessage }} ({{
                  translations.maxWords
                }})"
                [disabled]="isProcessingOCR"
                class="w-full p-2 pb-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 select-text disabled:opacity-50 disabled:cursor-not-allowed min-h-[80px] max-h-[400px] textarea-with-handle"
                rows="2"
                (input)="onTextInput($event, 'user')"
                (focus)="onTextareaFocus('user')"
                (blur)="onTextareaBlur('user')"
                inputmode="text"
                enterkeyhint="done"
                style="
                  user-select: text;
                  -webkit-user-select: text;
                  -moz-user-select: text;
                  -ms-user-select: text;
                  overflow-y: auto;
                  scrollbar-width: thin;
                  position: relative;
                "
              ></textarea>
              <!-- Resize Handle Indicator - positioned at edge of textarea -->
              <!-- Inline styles removed - handled dynamically by TypeScript for mobile compatibility -->
              <div class="resize-handle-indicator">
                <div class="resize-handle-line"></div>
                <div class="resize-handle-line"></div>
                <div class="resize-handle-line"></div>
              </div>
              <div
                class="text-xs text-gray-500 mt-1 text-right"
                style="pointer-events: none; position: relative; z-index: 1"
              >
                {{ countWords(userTextInput) }}/200 {{ translations.words }}
              </div>
            </div>
            <!-- Camera and Translate Buttons -->
            <div class="mt-2 flex justify-between w-full">
              <div class="flex gap-2">
                <button
                  (click)="translateUserText()"
                  [disabled]="
                    !userTextInput.trim() ||
                    isProcessingTranslation ||
                    isProcessingOCR
                  "
                  class="w-10 h-8 md:w-12 md:h-10 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2 text-xs md:text-sm font-bold"
                  title="{{ translations.translate }}"
                >
                  A⇄Я
                </button>
                <button
                  (click)="readImageFromUserSection()"
                  [disabled]="isProcessingTranslation || isProcessingOCR"
                  class="w-10 h-8 md:w-12 md:h-10 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                  title="{{ translations.readImage }}"
                >
                  <svg
                    class="w-5 h-5 md:w-6 md:h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                  </svg>
                </button>
              </div>
              <!-- Skip Audio Toggle (User Section - Blue) -->
              <button
                (click)="toggleSkipAudioGeneration()"
                [class.bg-blue-600]="!skipAudioGeneration()"
                [class.bg-red-600]="skipAudioGeneration()"
                [class.hover:bg-blue-700]="!skipAudioGeneration()"
                [class.hover:bg-red-700]="skipAudioGeneration()"
                class="w-10 h-8 md:w-12 md:h-10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                [title]="skipAudioGeneration() ? 'Audio: OFF' : 'Audio: ON'"
              >
                <div class="flex items-center justify-center relative">
                  <svg
                    class="w-5 h-5 md:w-6 md:h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    ></path>
                  </svg>
                  <svg
                    *ngIf="skipAudioGeneration()"
                    class="w-5 h-5 md:w-6 md:h-6 absolute"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                    ></path>
                  </svg>
                </div>
              </button>
            </div>

            <!-- Inline image source menu (user) -->
            <div *ngIf="isUserImageMenuOpen" class="mt-2 w-full">
              <div class="border rounded-md overflow-hidden shadow-sm">
                <button
                  class="w-full px-4 py-2 text-left hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="isProcessingOCR"
                  (click)="
                    captureAndReadText(
                      getUserLanguageName(selectedUserLanguage()),
                      'camera'
                    )
                  "
                >
                  Take Photo
                </button>
                <button
                  class="w-full px-4 py-2 text-left hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="isProcessingOCR"
                  (click)="
                    captureAndReadText(
                      getUserLanguageName(selectedUserLanguage()),
                      'gallery'
                    )
                  "
                >
                  Choose from Gallery
                </button>
                <button
                  class="w-full px-4 py-2 text-left text-gray-500 hover:bg-gray-50"
                  (click)="isUserImageMenuOpen = false"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Current listening status -->
        <div
          *ngIf="recording && currentLane === 'user'"
          class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg"
        >
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            <span class="text-sm text-blue-700"
              >Listening for
              {{ getUserLanguageName(selectedUserLanguage()) }}
              speech...</span
            >
            <div
              *ngIf="isPlayingAudio"
              class="flex items-center gap-1 text-blue-600"
            >
              <div class="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span class="text-xs">Playing audio...</span>
            </div>
          </div>
          <div class="mt-2 text-xs text-blue-600 font-medium">
            🎯 Please speak ONLY in
            {{ getUserLanguageName(selectedUserLanguage()) }} language
          </div>
          <div
            *ngIf="isAutoRestarting && currentLane === 'user'"
            class="mt-2 text-xs text-orange-600 font-medium"
          >
            🔄 Auto-restarting for next speech...
          </div>
          <div
            *ngIf="
              (currentEnText && currentEnText.trim()) ||
              (currentKyText && currentKyText.trim())
            "
            class="current-translation mt-2 text-sm text-gray-600 select-text"
            style="
              user-select: text;
              -webkit-user-select: text;
              -moz-user-select: text;
              -ms-user-select: text;
            "
          >
            <div class="mb-1" style="border: 2px solid red; padding: 5px">
              <div class="flex items-center justify-between mb-1">
                <div class="text-xs font-medium text-gray-700">
                  {{ getUserLanguageName(selectedUserLanguage()) }}:
                </div>
              </div>
              <div class="bg-white p-3 rounded border">
                <span
                  class="text-sm text-gray-900 select-text"
                  style="
                    user-select: text;
                    -webkit-user-select: text;
                    -moz-user-select: text;
                    -ms-user-select: text;
                    overflow-y: auto;
                    scrollbar-width: thin;
                  "
                >
                  {{ currentEnText }}
                </span>
              </div>
            </div>
            <div style="border: 2px solid blue; padding: 5px">
              <div class="flex items-center justify-between mb-1">
                <div class="text-xs font-medium text-gray-700">
                  {{ getPartnerLanguageName(selectedPartnerLanguage()) }}:
                </div>
              </div>
              <div class="bg-white p-3 rounded border">
                <span
                  class="text-sm text-gray-900 select-text"
                  style="
                    user-select: text;
                    -webkit-user-select: text;
                    -moz-user-select: text;
                    -ms-user-select: text;
                    overflow-y: auto;
                    scrollbar-width: thin;
                  "
                >
                  {{ currentKyText }}
                </span>
              </div>
              <div
                class="text-xs mt-1"
                [class]="
                  currentEnConfidence > 0.7
                    ? 'text-green-600'
                    : currentEnConfidence > 0.4
                    ? 'text-yellow-600'
                    : 'text-red-600'
                "
              >
                ({{ (currentEnConfidence * 100).toFixed(0) }}% confidence)
              </div>
            </div>
          </div>

          <!-- Diarization Results -->
          <div
            *ngIf="showDiarizationResults && diarizationResults.length > 0"
            class="diarization-results mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200"
          >
            <div class="flex items-center justify-between mb-3">
              <h3
                class="text-lg font-semibold text-blue-800 flex items-center gap-2"
              >
                <span>👥</span>
                Speaker Separation Results
              </h3>
              <button
                (click)="clearDiarizationResults()"
                class="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-300 hover:bg-blue-100 transition-colors"
              >
                Clear
              </button>
            </div>

            <div class="space-y-3">
              <div
                *ngFor="let segment of diarizationResults; let i = index"
                class="speaker-segment p-3 bg-white rounded-lg border-l-4 shadow-sm"
                [style.border-left-color]="getSpeakerColor(segment.speaker)"
              >
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-2">
                    <div
                      class="w-3 h-3 rounded-full"
                      [style.background-color]="
                        getSpeakerColor(segment.speaker)
                      "
                    ></div>
                    <span class="font-medium text-gray-700">{{
                      segment.speaker
                    }}</span>
                  </div>
                  <span class="text-xs text-gray-500">
                    {{ segment.start.toFixed(1) }}s -
                    {{ segment.end.toFixed(1) }}s
                  </span>
                </div>
                <div class="text-sm text-gray-800">{{ segment.text }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Conversation history -->
        <div
          id="en-conversation"
          class="space-y-3 max-h-64 md:max-h-96 overflow-y-auto"
        >
          <div
            *ngFor="
              let entry of showAllConversations
                ? enConversation()
                : enConversation().slice(0, 1)
            "
            class="conversation-item border rounded-lg p-3 bg-gray-50 select-text"
            style="
              user-select: text;
              -webkit-user-select: text;
              -moz-user-select: text;
              -ms-user-select: text;
            "
          >
            <div class="text-sm text-gray-500 mb-1 flex items-center gap-2">
              <span>{{ entry.timestamp | date : "short" }}</span>
              <span
                *ngIf="entry.confidence"
                class="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded"
              >
                {{ (entry.confidence * 100).toFixed(0) }}% confidence
              </span>
            </div>
            <div class="mb-2">
              <div class="p-2 bg-white rounded border">
                {{ entry.original }}
              </div>
            </div>
            <div>
              <div class="p-2 bg-white rounded border">
                {{ entry.translated }}
              </div>

              <!-- Recorded Audio Player - Original recorded speech -->
              <div *ngIf="entry.recordedAudioUrl" class="mt-2">
                <div class="text-xs text-gray-500 mb-1">
                  🎙️ {{ getLanguageName(entry.userLanguage) }} Recording:
                </div>
                <audio
                  [src]="entry.recordedAudioUrl"
                  controls
                  class="w-full"
                  controlsList="nodownload"
                ></audio>
              </div>

              <!-- Original Audio Player - Show when audio is available -->
              <div *ngIf="entry.originalAudioUrl" class="mt-2">
                <div class="text-xs text-gray-500 mb-1">
                  🔊 {{ getLanguageName(entry.userLanguage) }} Audio:
                </div>
                <audio
                  [src]="entry.originalAudioUrl"
                  controls
                  class="w-full"
                  controlsList="nodownload"
                ></audio>
              </div>

              <!-- Translated Audio Player -->
              <div *ngIf="entry.audioUrl" class="mt-2">
                <div class="text-xs text-gray-500 mb-1">
                  🔊 {{ getLanguageName(entry.targetLanguage) }} Audio:
                </div>
                <audio
                  [src]="entry.audioUrl"
                  controls
                  class="w-full"
                  controlsList="nodownload"
                ></audio>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Partner → User Lane -->
      <section
        *ngIf="showPartnerSection()"
        class="border rounded-xl p-3 md:p-4 shadow"
      >
        <div class="mb-4">
          <!-- Hamburger menu button and logo/title - positioned above toggle button -->
          <div class="content-header-inline flex items-center mb-3">
            <button
              type="button"
              id="content-menu-button-partner"
              class="content-hamburger-button flex-shrink-0"
              (click)="navigation.toggleMobileMenu($event)"
              (touchstart)="navigation.toggleMobileMenu($event)"
            >
              <svg
                class="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
            <!-- Spacer to push logo/title to center -->
            <div class="flex-1"></div>
            <!-- App Logo and Title - centered -->
            <div
              class="content-logo-title flex items-center gap-2 flex-shrink-0"
            >
              <img
                [src]="logoUrl"
                alt="TilHub Logo"
                class="content-logo w-10 h-10 object-contain"
              />
              <h1 class="content-title text-lg font-bold text-gray-900 m-0">
                {{ translations.translation || "Translation" }}
              </h1>
            </div>
            <!-- Spacer to balance the hamburger button on the left -->
            <div class="flex-1"></div>
          </div>
          <!-- Toggle Button replacing language direction label -->
          <button
            (click)="toggleBidirectionalSection()"
            type="button"
            class="w-full px-2 md:px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm md:text-base font-semibold transition-colors flex items-center justify-center gap-1 md:gap-2 mb-3 min-w-0 overflow-hidden"
            [title]="
              'Switch to ' +
              getUserLanguageName(selectedUserLanguage()) +
              ' → ' +
              getPartnerLanguageName(selectedPartnerLanguage())
            "
          >
            <svg
              class="w-4 h-4 md:w-5 md:h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <span
              class="font-mono text-[10px] md:text-sm break-words whitespace-normal text-center flex-1 min-w-0"
              style="word-break: break-word; hyphens: auto; line-height: 1.2"
              >{{ getPartnerLanguageName(selectedPartnerLanguage()) }} →
              {{ getUserLanguageName(selectedUserLanguage()) }}</span
            >
            <span
              *ngIf="isDevMode()"
              class="ml-2 text-xs text-orange-600 font-bold"
              >Dev</span
            >
          </button>
          <!-- Conversation Topic Input (Hidden) -->
          <div class="mb-3" *ngIf="false" style="display: none;">
            <div class="flex items-center gap-2">
              <input
                type="text"
                [value]="conversationTopic()"
                (input)="conversationTopic.set($any($event.target).value)"
                [placeholder]="
                  translations.conversationTopicPlaceholder ||
                  'Conversation topic (e.g., dental lecture, medical conference)'
                "
                class="flex-1 p-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxlength="50"
              />
              <button
                type="button"
                class="conversation-topic-info-btn flex-shrink-0 text-gray-400 hover:text-gray-600 focus:outline-none bg-transparent border-none p-0"
                [title]="
                  translations.conversationTopicHint ||
                  'Optional: Helps improve translation accuracy for specific topics'
                "
                (click)="showConversationTopicHelp()"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div class="flex justify-between items-center w-full">
            <!-- Microphone Toggle Button with Recording Indicator -->
            <div class="flex items-center gap-2">
              <button
                (click)="toggleRecording('partner')"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3"
                [class]="
                  recording && currentLane === 'partner'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                "
                title="{{ translations.start }}"
              >
                <!-- Microphone Icon -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  [class.animate-pulse]="recording && currentLane === 'partner'"
                >
                  <path
                    d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"
                  />
                  <path
                    d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"
                  />
                </svg>
              </button>
              <!-- Continuous Conversation Button -->
              <button
                (click)="openContinuousConversationModalForPartner()"
                type="button"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3 bg-green-600 hover:bg-green-700"
                title="{{
                  translations.continuousConversation ||
                    'Continuous Conversation'
                }}"
              >
                <!-- Chat/Conversation Icon -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
                  />
                  <path d="M7 9h2v2H7zm4 0h6v2h-6zm-4 4h6v2H7z" />
                </svg>
              </button>
              <!-- Bidirectional Conversation Button -->
              <button
                *ngIf="showPartnerSection()"
                (click)="openBidirectionalConversationModalForPartner()"
                type="button"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3 bg-green-600 hover:bg-green-700"
                title="{{
                  translations.bidirectionalConversation ||
                    'Bidirectional Conversation'
                }}"
              >
                <!-- Bidirectional Arrow Icon (<>) -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                >
                  <!-- Left arrow (<) -->
                  <path
                    d="M9 12L5 8M5 8L9 4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <!-- Right arrow (>) -->
                  <path
                    d="M15 12L19 8M19 8L15 4"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
              <!-- Broadcast Translation Button -->
              <button
                (click)="openBroadcastTranslationModalForPartner()"
                type="button"
                class="w-14 h-10 md:w-16 md:h-12 rounded-lg text-white flex-shrink-0 transition-all duration-200 flex items-center justify-center aspect-square min-w-14 min-h-10 md:min-w-16 md:min-h-12 max-w-14 max-h-10 md:max-w-16 md:max-h-12 px-2 py-3 bg-green-600 hover:bg-green-700"
                title="{{
                  translations.broadcastTranslation || 'Broadcast Translation'
                }}"
              >
                <!-- Radio Antenna Icon -->
                <svg
                  class="w-6 h-6 md:w-7 md:h-7"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <!-- Antenna Tower Base -->
                  <rect x="11" y="18" width="2" height="4" />
                  <!-- Antenna Mast -->
                  <line
                    x1="12"
                    y1="2"
                    x2="12"
                    y2="18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <!-- Antenna Top (dipole) -->
                  <line
                    x1="8"
                    y1="2"
                    x2="16"
                    y2="2"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                  />
                  <line
                    x1="8"
                    y1="2"
                    x2="8"
                    y2="4"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <line
                    x1="16"
                    y1="2"
                    x2="16"
                    y2="4"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                  />
                  <!-- Radiating Waves Left -->
                  <path
                    d="M12 18 Q8 16 4 14"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                  <path
                    d="M12 18 Q6 15 0 12"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                  <!-- Radiating Waves Right -->
                  <path
                    d="M12 18 Q16 16 20 14"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                  <path
                    d="M12 18 Q18 15 24 12"
                    stroke="currentColor"
                    stroke-width="1.5"
                    fill="none"
                    stroke-linecap="round"
                  />
                </svg>
              </button>
              <!-- Recording Indicator -->
              <span
                *ngIf="recording && currentLane === 'partner'"
                class="text-red-600 font-semibold text-sm md:text-base animate-pulse"
              >
                Recording...
              </span>
            </div>

            <!-- Right side buttons: Show All and Clear History -->
            <div class="flex gap-2 ml-2 md:ml-0">
              <!-- Show All Partner Conversations Button -->
              <button
                (click)="togglePartnerConversationVisibility()"
                class="w-10 h-8 md:w-12 md:h-10 rounded-lg border transition-colors duration-200 flex-shrink-0 flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                [class]="
                  showAllPartnerConversations
                    ? 'bg-green-600 text-white border-green-600 hover:bg-green-700'
                    : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                "
                title="{{ translations.showAll }}"
              >
                <svg
                  *ngIf="!showAllPartnerConversations"
                  class="w-4 h-4 md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  ></path>
                </svg>
                <svg
                  *ngIf="showAllPartnerConversations"
                  class="w-4 h-4 md:w-5 md:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                  ></path>
                </svg>
              </button>

              <!-- Clear History Button -->
              <button
                (click)="clearPartnerText()"
                class="w-10 h-8 md:w-12 md:h-10 rounded-lg bg-gray-500 text-white hover:bg-gray-600 flex-shrink-0 flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                title="{{ translations.clearHistory }}"
              >
                <svg
                  class="w-5 h-5 md:w-6 md:h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  ></path>
                </svg>
              </button>
            </div>
          </div>

          <!-- Translation Spinner - More Visible Under Buttons -->
          <div
            *ngIf="
              (isProcessingTranslation || isProcessingPostTranslation) &&
              !isProcessingAudioGeneration &&
              !isProcessingOCR &&
              currentLane === 'partner'
            "
            class="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-center gap-2"
          >
            <div
              class="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"
            ></div>
            <span class="text-orange-700 font-medium"
              >Translating to
              {{ getUserLanguageName(selectedUserLanguage()) }}...</span
            >
          </div>

          <!-- OCR Processing Spinner (Partner Section) -->
          <div
            *ngIf="isProcessingOCR && currentLane === 'partner'"
            class="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center gap-2"
          >
            <div
              class="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"
            ></div>
            <span class="text-purple-700 font-medium"
              >Reading image for
              {{ getPartnerLanguageName(selectedPartnerLanguage()) }}...</span
            >
          </div>

          <!-- Audio Generation Spinner (Second Cycle) - Blue background to distinguish from translation -->
          <div
            *ngIf="isProcessingAudioGeneration && currentLane === 'partner'"
            class="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center gap-2"
          >
            <div
              class="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"
            ></div>
            <span class="text-blue-700 font-medium"
              >🎵 Generating audio...</span
            >
          </div>

          <!-- Word Limit Error Message (Mobile-Friendly) -->
          <div
            *ngIf="wordLimitErrorMessage"
            class="mt-3 p-4 bg-red-50 border-2 border-red-500 rounded-lg"
          >
            <div class="flex items-start gap-3">
              <span class="text-2xl">⚠️</span>
              <div class="flex-1">
                <h3 class="text-red-800 font-bold text-lg mb-2">
                  {{ translations.wordLimitExceededTitle }}
                </h3>
                <p class="text-red-700 text-base mb-3">
                  {{ wordLimitErrorMessage }}
                </p>
                <div class="flex gap-2">
                  <button
                    *ngIf="!isIOS()"
                    (click)="onShowBuyWords()"
                    class="px-4 py-2 bg-red-600 text-white rounded-md font-semibold hover:bg-red-700"
                  >
                    {{ translations.buyMoreWords }}
                  </button>
                  <button
                    (click)="wordLimitErrorMessage = null"
                    class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md font-semibold hover:bg-gray-400"
                  >
                    {{ translations.dismiss }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Text Input Section for Partner -->
          <div class="mt-4 p-3 bg-gray-50 rounded-lg border">
            <div
              class="w-full relative"
              style="position: relative; overflow: visible"
            >
              <textarea
                #partnerTextArea
                [(ngModel)]="partnerTextInput"
                placeholder="{{ translations.typePartnerMessage }} ({{
                  translations.maxWords
                }})"
                [disabled]="isProcessingOCR"
                class="w-full p-2 pb-8 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 select-text disabled:opacity-50 disabled:cursor-not-allowed min-h-[80px] max-h-[400px] textarea-with-handle"
                rows="2"
                (input)="onTextInput($event, 'partner')"
                (focus)="onTextareaFocus('partner')"
                (blur)="onTextareaBlur('partner')"
                inputmode="text"
                enterkeyhint="done"
                style="
                  user-select: text;
                  -webkit-user-select: text;
                  -moz-user-select: text;
                  -ms-user-select: text;
                  overflow-y: auto;
                  scrollbar-width: thin;
                  position: relative;
                "
              ></textarea>
              <!-- Resize Handle Indicator - positioned at edge of textarea -->
              <!-- Inline styles removed - handled dynamically by TypeScript for mobile compatibility -->
              <div class="resize-handle-indicator">
                <div class="resize-handle-line"></div>
                <div class="resize-handle-line"></div>
                <div class="resize-handle-line"></div>
              </div>
              <div
                class="text-xs text-gray-500 mt-1 text-right"
                style="pointer-events: none; position: relative; z-index: 1"
              >
                {{ countWords(partnerTextInput) }}/200 {{ translations.words }}
              </div>
            </div>
            <!-- Translate and Camera Buttons -->
            <div class="mt-2 flex justify-between w-full">
              <div class="flex gap-2">
                <button
                  (click)="translatePartnerText()"
                  [disabled]="
                    !partnerTextInput.trim() ||
                    isProcessingTranslation ||
                    isProcessingOCR
                  "
                  class="w-10 h-8 md:w-12 md:h-10 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2 text-xs md:text-sm font-bold"
                  title="{{ translations.translate }}"
                >
                  A⇄Я
                </button>
                <button
                  (click)="readImageFromPartnerSection()"
                  [disabled]="isProcessingTranslation || isProcessingOCR"
                  class="w-10 h-8 md:w-12 md:h-10 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                  title="{{ translations.readImage }}"
                >
                  <svg
                    class="w-5 h-5 md:w-6 md:h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    ></path>
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    ></path>
                  </svg>
                </button>
              </div>
              <!-- Skip Audio Toggle (Partner Section - Green) -->
              <button
                (click)="toggleSkipAudioGeneration()"
                [class.bg-green-600]="!skipAudioGeneration()"
                [class.bg-red-600]="skipAudioGeneration()"
                [class.hover:bg-green-700]="!skipAudioGeneration()"
                [class.hover:bg-red-700]="skipAudioGeneration()"
                class="w-10 h-8 md:w-12 md:h-10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center aspect-square min-w-10 min-h-8 md:min-w-12 md:min-h-10 max-w-10 max-h-8 md:max-w-12 md:max-h-10 px-2"
                [title]="skipAudioGeneration() ? 'Audio: OFF' : 'Audio: ON'"
              >
                <div class="flex items-center justify-center relative">
                  <svg
                    class="w-5 h-5 md:w-6 md:h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                    ></path>
                  </svg>
                  <svg
                    *ngIf="skipAudioGeneration()"
                    class="w-5 h-5 md:w-6 md:h-6 absolute"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="3"
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                    ></path>
                  </svg>
                </div>
              </button>
            </div>

            <!-- Inline image source menu (partner) -->
            <div *ngIf="isPartnerImageMenuOpen" class="mt-2 w-full">
              <div class="border rounded-md overflow-hidden shadow-sm">
                <button
                  class="w-full px-4 py-2 text-left hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="isProcessingOCR"
                  (click)="
                    captureAndReadText(
                      getPartnerLanguageName(selectedPartnerLanguage()),
                      'camera'
                    )
                  "
                >
                  Take Photo
                </button>
                <button
                  class="w-full px-4 py-2 text-left hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  [disabled]="isProcessingOCR"
                  (click)="
                    captureAndReadText(
                      getPartnerLanguageName(selectedPartnerLanguage()),
                      'gallery'
                    )
                  "
                >
                  Choose from Gallery
                </button>
                <button
                  class="w-full px-4 py-2 text-left text-gray-500 hover:bg-gray-50"
                  (click)="isPartnerImageMenuOpen = false"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Current listening status -->
        <div
          *ngIf="recording && currentLane === 'partner'"
          class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg"
        >
          <div class="flex items-center gap-2">
            <div class="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span class="text-sm text-green-700"
              >Listening for
              {{ getPartnerLanguageName(selectedPartnerLanguage()) }}
              speech...</span
            >
            <div
              *ngIf="isPlayingAudio"
              class="flex items-center gap-1 text-green-600"
            >
              <div
                class="w-2 h-2 bg-green-500 rounded-full animate-pulse"
              ></div>
              <span class="text-xs">Playing audio...</span>
            </div>
          </div>
          <div class="mt-2 text-xs text-green-600 font-medium">
            🎯 Please speak ONLY in
            {{ getPartnerLanguageName(selectedPartnerLanguage()) }} language
          </div>
          <div
            *ngIf="isAutoRestarting && currentLane === 'partner'"
            class="mt-2 text-xs text-orange-600 font-medium"
          >
            🔄 Auto-restarting for next speech...
          </div>
          <div
            *ngIf="
              (currentKyInText && currentKyInText.trim()) ||
              (currentEnOutText && currentEnOutText.trim())
            "
            class="current-translation mt-2 text-sm text-gray-600 select-text"
            style="
              user-select: text;
              -webkit-user-select: text;
              -moz-user-select: text;
              -ms-user-select: text;
            "
          >
            <div class="mb-1" style="border: 2px solid green; padding: 5px">
              <div class="flex items-center justify-between mb-1">
                <div class="text-xs font-medium text-gray-700">
                  {{ getPartnerLanguageName(selectedPartnerLanguage()) }}:
                </div>
              </div>
              <div class="bg-white p-3 rounded border">
                <span
                  class="text-sm text-gray-900 select-text"
                  style="
                    user-select: text;
                    -webkit-user-select: text;
                    -moz-user-select: text;
                    -ms-user-select: text;
                    overflow-y: auto;
                    scrollbar-width: thin;
                  "
                >
                  {{ currentKyInText }}
                </span>
              </div>
            </div>
            <div style="border: 2px solid orange; padding: 5px">
              <div class="flex items-center justify-between mb-1">
                <div class="text-xs font-medium text-gray-700">
                  {{ getUserLanguageName(selectedUserLanguage()) }}:
                </div>
              </div>
              <div class="bg-white p-3 rounded border">
                <span
                  class="text-sm text-gray-900 select-text"
                  style="
                    user-select: text;
                    -webkit-user-select: text;
                    -moz-user-select: text;
                    -ms-user-select: text;
                    overflow-y: auto;
                    scrollbar-width: thin;
                  "
                >
                  {{ currentEnOutText }}
                </span>
              </div>
              <div
                class="text-xs mt-1"
                [class]="
                  currentKyConfidence > 0.7
                    ? 'text-green-600'
                    : currentKyConfidence > 0.4
                    ? 'text-yellow-600'
                    : 'text-red-600'
                "
              >
                ({{ (currentKyConfidence * 100).toFixed(0) }}% confidence)
              </div>
            </div>
          </div>
        </div>

        <!-- Conversation history -->
        <div
          id="ky-conversation"
          class="space-y-3 max-h-64 md:max-h-96 overflow-y-auto"
        >
          <div
            *ngFor="
              let entry of showAllPartnerConversations
                ? kyConversation()
                : kyConversation().slice(0, 1)
            "
            class="conversation-item border rounded-lg p-3 bg-gray-50 select-text"
            style="
              user-select: text;
              -webkit-user-select: text;
              -moz-user-select: text;
              -ms-user-select: text;
            "
          >
            <div class="text-sm text-gray-500 mb-1 flex items-center gap-2">
              <span>{{ entry.timestamp | date : "short" }}</span>
              <span
                *ngIf="entry.confidence"
                class="text-xs bg-green-100 text-green-800 px-2 py-1 rounded"
              >
                {{ (entry.confidence * 100).toFixed(0) }}% confidence
              </span>
            </div>
            <div class="mb-2">
              <div class="p-2 bg-white rounded border">
                {{ entry.original }}
              </div>
            </div>
            <div>
              <div class="p-2 bg-white rounded border">
                {{ entry.translated }}
              </div>

              <!-- Recorded Audio Player - Original recorded speech -->
              <div *ngIf="entry.recordedAudioUrl" class="mt-2">
                <div class="text-xs text-gray-500 mb-1">
                  🎙️ {{ getLanguageName(entry.userLanguage) }} Recording:
                </div>
                <audio
                  [src]="entry.recordedAudioUrl"
                  controls
                  class="w-full"
                  controlsList="nodownload"
                ></audio>
              </div>

              <!-- Original Audio Player - Show when audio is available -->
              <div *ngIf="entry.originalAudioUrl" class="mt-2">
                <div class="text-xs text-gray-500 mb-1">
                  🔊 {{ getLanguageName(entry.userLanguage) }} Audio:
                </div>
                <audio
                  [src]="entry.originalAudioUrl"
                  controls
                  class="w-full"
                  controlsList="nodownload"
                ></audio>
              </div>

              <!-- Translated Audio Player -->
              <div *ngIf="entry.audioUrl" class="mt-2">
                <div class="text-xs text-gray-500 mb-1">
                  🔊 {{ getLanguageName(entry.targetLanguage) }} Audio:
                </div>
                <audio
                  [src]="entry.audioUrl"
                  controls
                  class="w-full"
                  controlsList="nodownload"
                ></audio>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>

  <!-- Conversation History Modal -->
  <app-conversation-history-modal
    [isOpen]="showHistoryModal"
    (close)="onCloseHistory()"
    (playAudio)="onPlayHistoryAudio($event)"
    (clearMainConversations)="clearAllConversations()"
  ></app-conversation-history-modal>

  <!-- Word Usage Modal -->
  <app-word-usage-modal
    [isOpen]="showWordUsageModal"
    (close)="onCloseWordUsage()"
  ></app-word-usage-modal>

  <!-- Image Usage Modal -->
  <app-image-usage-modal
    [isOpen]="showImageUsageModal"
    (close)="onCloseImageUsage()"
  ></app-image-usage-modal>

  <!-- Buy Words Modal -->
  <app-buy-words-modal
    *ngIf="showBuyWordsModal"
    [isOpen]="showBuyWordsModal"
    [preSelectedPackageId]="preSelectedPackageId"
    [preSelectedImagePackageId]="preSelectedImagePackageId"
    (close)="onCloseBuyWords()"
    (wordsPurchased)="onWordsPurchased($event)"
    (imagesPurchased)="onImagesPurchased($event)"
    (paymentSuccessMessage)="showPaymentSuccessBannerMessage($event)"
  ></app-buy-words-modal>

  <!-- Analytics Modal -->
  <app-analytics
    [isOpen]="showAnalyticsModal"
    (close)="onCloseAnalytics()"
  ></app-analytics>

  <!-- Continuous Conversation Modal -->
  <app-continuous-conversation-modal
    [isOpen]="showContinuousConversationModal"
    [sourceLanguage]="continuousConversationSourceLanguage"
    [targetLanguage]="continuousConversationTargetLanguage"
    [conversationTopic]="conversationTopic()"
    (close)="onCloseContinuousConversation()"
    (showBuyWords)="onShowBuyWords()"
  ></app-continuous-conversation-modal>

  <!-- Bidirectional Conversation Modal -->
  <app-bidirectional-conversation-modal
    [isOpen]="showBidirectionalConversationModal"
    [sourceLanguage]="bidirectionalConversationSourceLanguage"
    [targetLanguage]="bidirectionalConversationTargetLanguage"
    [conversationTopic]="conversationTopic()"
    (close)="onCloseBidirectionalConversation()"
    (showBuyWords)="onShowBuyWords()"
  ></app-bidirectional-conversation-modal>

  <!-- Broadcast Translation Modal -->
  <app-broadcast-translation-modal
    [isOpen]="showBroadcastTranslationModal"
    [sourceLanguage]="broadcastSourceLanguage"
    [targetLanguage]="broadcastTargetLanguage"
    [conversationTopic]="conversationTopic()"
    (close)="onCloseBroadcastTranslation()"
    (showBuyWords)="onShowBuyWords()"
  ></app-broadcast-translation-modal>

  <!-- Language Settings Modal -->
  <div
    *ngIf="showLanguageSettingsModal"
    class="modal-overlay"
    (click)="onCloseLanguageSettings()"
    style="z-index: 9999 !important"
  >
    <div
      class="modal-content"
      (click)="$event.stopPropagation()"
      style="
        width: 90vw;
        max-width: 500px;
        height: auto;
        max-height: 80vh;
        padding: 1.5rem;
        overflow-y: auto;
      "
    >
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold text-gray-900">Language Settings</h2>
        <button
          (click)="onCloseLanguageSettings()"
          class="text-gray-400 hover:text-gray-600"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <!-- UI Language Selection -->
      <div class="mb-8">
        <label class="block text-sm font-medium text-gray-700 mb-3">
          Select Interface Language:
        </label>
        <div class="space-y-3">
          <label
            *ngFor="let lang of availableUILanguages"
            class="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200"
            [class.bg-blue-50]="currentUILanguage() === lang.value"
            [class.border-blue-300]="currentUILanguage() === lang.value"
          >
            <input
              type="radio"
              [value]="lang.value"
              [checked]="currentUILanguage() === lang.value"
              (change)="onUILanguageChange(lang.value)"
              class="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 mr-3"
            />
            <div class="flex-1">
              <div class="font-medium text-gray-900">{{ lang.label }}</div>
              <div class="text-sm text-gray-500">{{ lang.nativeName }}</div>
            </div>
            <div class="text-lg ml-4 mr-2">{{ lang.flag }}</div>
          </label>
        </div>
        <p class="text-xs text-gray-500 mt-2">
          This changes the language of buttons, menus, and interface elements.
        </p>
      </div>

      <!-- Save Button -->
      <div class="flex justify-end mt-6 mb-4">
        <button
          (click)="onCloseLanguageSettings()"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          Save Settings
        </button>
      </div>
    </div>
  </div>

  <!-- Keyboard Accessory Bar (Above Keyboard) -->
  <div
    *ngIf="showKeyboardToolbar && isMobile"
    class="keyboard-toolbar"
    [class.keyboard-toolbar-visible]="showKeyboardToolbar"
  >
    <button
      (click)="dismissKeyboardWithToolbar()"
      type="button"
      style="
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
      "
    >
      ✓ Done Typing
    </button>
  </div>
</div>
